import {cn} from '@sollapay/ui';
import {Icon, Typography} from '@sollapay/ui/components';
import {useIsGuestSession} from '@sollapay/web-core';
import {useTranslation} from 'react-i18next';

import {
  useCurrentTrustAccount,
  useTrustAccountPermissions
} from '@/features/trust-accounts/providers';
import {getTrustAccountBannerMode} from '@/features/trust-accounts/utils';

import type {TrustAccountBannerMode} from '@/features/trust-accounts/utils';
import type {IconName} from '@sollapay/ui/components';
import type {FC} from 'react';

interface BannerModeConfig {
  icon: IconName;
  containerClassName: string;
  textClassName: string;
  detailClassName: string;
  labelKey: string;
  detailWithTrusteeKey: string;
  detailWithoutTrusteeKey: string;
}

const VIEWER_MODE: BannerModeConfig = {
  icon: 'view',
  containerClassName: 'bg-background-quaternary',
  textClassName: 'text-fg',
  detailClassName: 'text-fg-secondary',
  labelKey: 'permissionBanner.viewModeLabel',
  detailWithTrusteeKey: 'permissionBanner.detailWithTrustee',
  detailWithoutTrusteeKey: 'permissionBanner.detailWithoutTrustee'
};

/** Icons match the trust list's access sections, so a role reads the same on both screens. */
const BANNER_MODES: Record<TrustAccountBannerMode, BannerModeConfig> = {
  guest: {
    ...VIEWER_MODE,
    detailWithTrusteeKey: 'permissionBanner.guestDetailWithTrustee',
    detailWithoutTrusteeKey: 'permissionBanner.guestDetailWithoutTrustee'
  },
  viewer: VIEWER_MODE,
  operator: {
    icon: 'tool',
    containerClassName: 'bg-brand-primary',
    textClassName: 'text-fg_on-brand',
    detailClassName: 'text-fg_on-brand opacity-90',
    labelKey: 'permissionBanner.operatorModeLabel',
    detailWithTrusteeKey: 'permissionBanner.operatorDetailWithTrustee',
    detailWithoutTrusteeKey: 'permissionBanner.operatorDetailWithoutTrustee'
  }
};

export const TrustAccountAccessBanner: FC = () => {
  const {t} = useTranslation('trustAccounts');
  const {name, lawyerName, isTrustee} = useCurrentTrustAccount();
  const isGuest = useIsGuestSession();
  const permissions = useTrustAccountPermissions();
  const mode = getTrustAccountBannerMode({isTrustee, isGuest, permissions});

  if (!mode) return null;

  const config = BANNER_MODES[mode];

  return (
    <div
      data-slot="access-banner"
      data-mode={mode}
      className={cn(
        'flex items-center justify-center gap-2.5 px-4 py-2 sm:px-6',
        config.containerClassName
      )}
    >
      <Icon
        name={config.icon}
        width={18}
        height={18}
        className={cn('shrink-0', config.textClassName)}
      />
      <div className="flex min-w-0 flex-wrap items-baseline justify-center gap-x-1.5 text-center">
        <Typography
          size="sm"
          weight="medium"
          className={cn('whitespace-normal', config.textClassName)}
        >
          {t(config.labelKey)} {name}
        </Typography>
        <Typography size="sm" className={cn('whitespace-normal', config.detailClassName)}>
          {lawyerName
            ? t(config.detailWithTrusteeKey, {name: lawyerName})
            : t(config.detailWithoutTrusteeKey)}
        </Typography>
      </div>
    </div>
  );
};
